const HEATMAP_CELL_METERS = 60;
export const HEATMAP_RADIUS_PX = 55;

export const HEATMAP_GRADIENT = [
  "#1e3a8a",
  "#3b82f6",
  "#a855f7",
  "#f97316",
  "#ef4444",
];

const canMergeClusters = (a, b, cellMeters) =>
  Math.max(a.maxX, b.maxX) - Math.min(a.minX, b.minX) <= cellMeters &&
  Math.max(a.maxY, b.maxY) - Math.min(a.minY, b.minY) <= cellMeters;

const mergeClusters = (a, b) => {
  const nextIntensity = a.intensity + b.intensity;

  return {
    lat: (a.lat * a.intensity + b.lat * b.intensity) / nextIntensity,
    lng: (a.lng * a.intensity + b.lng * b.intensity) / nextIntensity,
    intensity: nextIntensity,
    minX: Math.min(a.minX, b.minX),
    maxX: Math.max(a.maxX, b.maxX),
    minY: Math.min(a.minY, b.minY),
    maxY: Math.max(a.maxY, b.maxY),
  };
};

export const aggregateHeatmapPoints = (points, cellMeters = HEATMAP_CELL_METERS) => {
  if (!Array.isArray(points) || points.length === 0) {
    return { points: [], maxIntensity: 0 };
  }

  const validPoints = points
    .map(({ lat, lng, intensity = 1 }) => ({
      lat: Number(lat),
      lng: Number(lng),
      intensity: Number(intensity) || 1,
    }))
    .filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng));

  if (!validPoints.length) {
    return { points: [], maxIntensity: 0 };
  }

  const referenceLat =
    validPoints.reduce((sum, point) => sum + point.lat, 0) / validPoints.length;
  const metersPerLng =
    111320 * Math.cos((referenceLat * Math.PI) / 180) || 111320;

  const toProjectedPoint = (point) => ({
    ...point,
    x: point.lng * metersPerLng,
    y: point.lat * 111320,
  });

  const clusters = [];

  validPoints.map(toProjectedPoint).forEach((point) => {
    const cluster = clusters.find((candidate) => {
      const minX = Math.min(candidate.minX, point.x);
      const maxX = Math.max(candidate.maxX, point.x);
      const minY = Math.min(candidate.minY, point.y);
      const maxY = Math.max(candidate.maxY, point.y);

      return maxX - minX <= cellMeters && maxY - minY <= cellMeters;
    });

    if (!cluster) {
      clusters.push({
        lat: point.lat,
        lng: point.lng,
        intensity: point.intensity,
        minX: point.x,
        maxX: point.x,
        minY: point.y,
        maxY: point.y,
      });
      return;
    }

    const nextIntensity = cluster.intensity + point.intensity;
    cluster.lat =
      (cluster.lat * cluster.intensity + point.lat * point.intensity) /
      nextIntensity;
    cluster.lng =
      (cluster.lng * cluster.intensity + point.lng * point.intensity) /
      nextIntensity;
    cluster.intensity = nextIntensity;
    cluster.minX = Math.min(cluster.minX, point.x);
    cluster.maxX = Math.max(cluster.maxX, point.x);
    cluster.minY = Math.min(cluster.minY, point.y);
    cluster.maxY = Math.max(cluster.maxY, point.y);
  });

  let mergedAny = true;
  while (mergedAny) {
    mergedAny = false;

    for (let i = 0; i < clusters.length; i += 1) {
      for (let j = i + 1; j < clusters.length; j += 1) {
        if (!canMergeClusters(clusters[i], clusters[j], cellMeters)) continue;

        clusters[i] = mergeClusters(clusters[i], clusters[j]);
        clusters.splice(j, 1);
        mergedAny = true;
        break;
      }

      if (mergedAny) break;
    }
  }

  const aggregated = clusters.map(({ lat, lng, intensity }) => ({
    lat,
    lng,
    intensity,
  }));

  const maxIntensity = aggregated.reduce(
    (max, point) => Math.max(max, point.intensity || 0),
    0
  );

  return { points: aggregated, maxIntensity };
};

export const buildSelectedHeatmapSourcePoints = ({
  playedPoints = [],
  played90SecPoints = [],
  reachedEndPoints = [],
  nextStepsPoints = [],
  showPlayed = false,
  showPlayed90Sec = false,
  showReachedEnd = false,
  showNextSteps = false,
}) => {
  const sourcePoints = [];

  if (showPlayed) {
    sourcePoints.push(...playedPoints.map((p) => ({ ...p, intensity: 1 })));
  }
  if (showPlayed90Sec) {
    sourcePoints.push(...played90SecPoints.map((p) => ({ ...p, intensity: 1 })));
  }
  if (showReachedEnd) {
    sourcePoints.push(...reachedEndPoints.map((p) => ({ ...p, intensity: 1 })));
  }
  if (showNextSteps) {
    sourcePoints.push(...nextStepsPoints.map((p) => ({ ...p, intensity: 1 })));
  }

  return sourcePoints;
};

const hexToRgb = (hex) => {
  const value = String(hex || "").replace("#", "");
  const intValue = parseInt(value, 16);

  return {
    r: (intValue >> 16) & 255,
    g: (intValue >> 8) & 255,
    b: intValue & 255,
  };
};

const interpolateColor = (from, to, amount) => ({
  r: Math.round(from.r + (to.r - from.r) * amount),
  g: Math.round(from.g + (to.g - from.g) * amount),
  b: Math.round(from.b + (to.b - from.b) * amount),
});

export const getHeatmapColor = (intensity, maxIntensity) => {
  const normalized =
    maxIntensity > 1
      ? Math.max(0, Math.min(1, (Number(intensity || 1) - 1) / (maxIntensity - 1)))
      : 0;
  const segmentCount = HEATMAP_GRADIENT.length - 1;
  const scaled = normalized * segmentCount;
  const segmentIndex = Math.min(segmentCount - 1, Math.floor(scaled));
  const segmentAmount = scaled - segmentIndex;

  return interpolateColor(
    hexToRgb(HEATMAP_GRADIENT[segmentIndex]),
    hexToRgb(HEATMAP_GRADIENT[segmentIndex + 1]),
    segmentAmount
  );
};
